import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Badge,
  Button,
  Spinner,
} from '@agion/ui'
import { analyzeLogEntry } from '@/core/observability_api_client'
import type { AnalyzeLogResponse, LogEntry } from '@/core/observability_types'

interface LogAnalysisDialogProps {
  entry: LogEntry | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'bg-red-600 text-white',
  high: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  low: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
}

export const LogAnalysisDialog: React.FC<LogAnalysisDialogProps> = ({ entry, open, onOpenChange }) => {
  const { t } = useTranslation('pages')
  const [result, setResult] = useState<AnalyzeLogResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open || !entry) return
    setResult(null)
    setError(null)
    setLoading(true)

    analyzeLogEntry({
      logLine: entry.line,
      labels: entry.labels,
      timestamp: entry.timestamp.toISOString(),
    })
      .then(setResult)
      .catch(err => setError(err?.message || String(err)))
      .finally(() => setLoading(false))
  }, [open, entry])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('observability.analysisTitle', 'Error Analysis')}</DialogTitle>
          <DialogDescription>
            {entry?.labels['app'] || entry?.labels['container'] || ''}
            {/* eslint-disable-next-line i18next/no-literal-string */}
            {' \u2014 '}
            {entry?.timestamp.toLocaleString()}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Spinner className="mx-auto mb-3" />
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {t('observability.analyzing', 'Analyzing error...')}
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-4">
            <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
          </div>
        )}

        {result && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                {t('observability.severity', 'Severity')}:
              </span>
              <Badge className={SEVERITY_COLORS[result.severity] || SEVERITY_COLORS['medium']}>
                {result.severity.toUpperCase()}
              </Badge>
            </div>

            <div>
              <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('observability.rootCause', 'Root Cause')}
              </h4>
              <p className="text-sm text-slate-600 dark:text-slate-400">{result.rootCause}</p>
            </div>

            <div>
              <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('observability.analysis', 'Analysis')}
              </h4>
              <p className="text-sm text-slate-600 dark:text-slate-400">{result.analysis}</p>
            </div>

            <div>
              <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('observability.suggestion', 'Suggestion')}
              </h4>
              <p className="text-sm text-slate-600 dark:text-slate-400">{result.suggestion}</p>
            </div>

            {result.sourceContext && (
              <div>
                <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('observability.sourceCode', 'Source Code')}
                </h4>
                <pre className="text-xs bg-slate-900 text-slate-100 p-3 rounded-lg overflow-x-auto max-h-64 overflow-y-auto">
                  {result.sourceContext}
                </pre>
              </div>
            )}

            <div className="pt-2 flex justify-end">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                {t('common.close', 'Close')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
